import React from 'react';
import ProtectedLink from '../ProtectedLink';
import { useAuthStore } from '../../store/authStore';
import { useAuthModalStore } from '../../store/authModalStore';
import {
  MdArrowForward,
  MdVerifiedUser,
  MdLocalShipping,
  MdCheck,
  MdTrendingUp,
} from 'react-icons/md';
import '../../styles/home/HeroSection.css';

const HeroSection: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const openRegister = useAuthModalStore((s) => s.openRegister);

  return (
    <section className="hero-section">
      <div className="hero-section__container">
        {/* ── Left Content ── */}
        <div className="hero-section__content">
          <div className="hero-section__badge">
            <MdVerifiedUser className="hero-section__badge-icon" />
            <span>Trusted by 500+ retailers</span>
          </div>

          <h1 className="hero-section__heading">
            Smarter{' '}
            <span className="hero-section__heading--accent">Distribution,</span>
            <br />
            Built for Scale
          </h1>

          <p className="hero-section__subtitle">
            Streamline your entire supply chain from warehouse to doorstep. Real-time
            tracking, intelligent routing, and automated inventory management — all
            in one platform.
          </p>

          {/* CTA Buttons */}
          <div className="hero-section__ctas">
            <ProtectedLink to="/products" className="hero-section__cta hero-section__cta--primary">
              Explore Products <MdArrowForward />
            </ProtectedLink>
            {!isAuthenticated && (
              <button className="hero-section__cta hero-section__cta--outline" onClick={openRegister}>
                Become a VIN Partner
              </button>
            )}
          </div>

          {/* Trust Line */}
          <div className="hero-section__trust">
            <span className="hero-section__trust-label">
              Trusted by leading retailers across the region
            </span>
            <div className="hero-section__trust-logos">
              <span className="hero-section__trust-dot" />
              <span className="hero-section__trust-dot" />
              <span className="hero-section__trust-dot" />
              <span className="hero-section__trust-dot" />
              <span className="hero-section__trust-dot" />
            </div>
          </div>
        </div>

        {/* ── Right Visual — Dashboard Preview ── */}
        <div className="hero-section__visual" aria-hidden="true">
          {/* Main Dashboard Card */}
          <div className="hero-section__dashboard">
            {/* Dashboard Header */}
            <div className="hero-section__dash-header">
              <div>
                <span className="hero-section__dash-title">Order Dashboard</span>
                <span className="hero-section__dash-subtitle">Real-time overview</span>
              </div>
              <span className="hero-section__dash-live">
                <span className="hero-section__dash-live-dot" />
                Live
              </span>
            </div>

            {/* Stats Row */}
            <div className="hero-section__dash-stats">
              <div className="hero-section__dash-stat">
                <span className="hero-section__dash-stat-value">1,284</span>
                <span className="hero-section__dash-stat-label">Total Orders</span>
              </div>
              <div className="hero-section__dash-stat">
                <span className="hero-section__dash-stat-value hero-section__dash-stat-value--green">96.2%</span>
                <span className="hero-section__dash-stat-label">Fulfilled</span>
              </div>
              <div className="hero-section__dash-stat">
                <span className="hero-section__dash-stat-value hero-section__dash-stat-value--blue">847</span>
                <span className="hero-section__dash-stat-label">In Transit</span>
              </div>
            </div>

            {/* Order Table */}
            <div className="hero-section__dash-table">
              <div className="hero-section__dash-table-head">
                <span>Order ID</span>
                <span>Route</span>
                <span>Status</span>
                <span>ETA</span>
              </div>
              <div className="hero-section__dash-table-row">
                <span className="hero-section__dash-table-id">#VN-4821</span>
                <span className="hero-section__dash-table-route">Dubai → Sharjah</span>
                <span className="hero-section__dash-table-status hero-section__dash-table-status--transit">
                  <MdLocalShipping /> In Transit
                </span>
                <span>12 min</span>
              </div>
              <div className="hero-section__dash-table-row">
                <span className="hero-section__dash-table-id">#VN-4820</span>
                <span className="hero-section__dash-table-route">Abu Dhabi → Al Ain</span>
                <span className="hero-section__dash-table-status hero-section__dash-table-status--delivered">
                  <MdCheck /> Delivered
                </span>
                <span>Done</span>
              </div>
              <div className="hero-section__dash-table-row">
                <span className="hero-section__dash-table-id">#VN-4819</span>
                <span className="hero-section__dash-table-route">Riyadh → Jeddah</span>
                <span className="hero-section__dash-table-status hero-section__dash-table-status--processing">
                  Processing
                </span>
                <span>45 min</span>
              </div>
            </div>

            {/* Mini Bar Chart */}
            <div className="hero-section__dash-chart">
              <div className="hero-section__dash-chart-header">
                <span className="hero-section__dash-chart-title">Weekly Deliveries</span>
                <span className="hero-section__dash-chart-trend">
                  <MdTrendingUp /> +18.2%
                </span>
              </div>
              <div className="hero-section__dash-chart-bars">
                <div className="hero-section__dash-chart-col">
                  <div className="hero-section__dash-chart-bar" style={{ height: '45%' }} />
                  <span>Mon</span>
                </div>
                <div className="hero-section__dash-chart-col">
                  <div className="hero-section__dash-chart-bar" style={{ height: '68%' }} />
                  <span>Tue</span>
                </div>
                <div className="hero-section__dash-chart-col">
                  <div className="hero-section__dash-chart-bar" style={{ height: '55%' }} />
                  <span>Wed</span>
                </div>
                <div className="hero-section__dash-chart-col">
                  <div className="hero-section__dash-chart-bar hero-section__dash-chart-bar--active" style={{ height: '85%' }} />
                  <span>Thu</span>
                </div>
                <div className="hero-section__dash-chart-col">
                  <div className="hero-section__dash-chart-bar" style={{ height: '72%' }} />
                  <span>Fri</span>
                </div>
                <div className="hero-section__dash-chart-col">
                  <div className="hero-section__dash-chart-bar" style={{ height: '90%' }} />
                  <span>Sat</span>
                </div>
                <div className="hero-section__dash-chart-col">
                  <div className="hero-section__dash-chart-bar" style={{ height: '40%' }} />
                  <span>Sun</span>
                </div>
              </div>
            </div>
          </div>

          {/* Floating Revenue Card */}
          <div className="hero-section__float-card hero-section__float-card--revenue">
            <span className="hero-section__float-label">Monthly Revenue</span>
            <span className="hero-section__float-value">$1.2M</span>
            <span className="hero-section__float-change">
              <MdTrendingUp /> +12.5%
            </span>
          </div>

          {/* Floating Delivery Card */}
          <div className="hero-section__float-card hero-section__float-card--delivery">
            <span className="hero-section__float-icon">
              <MdLocalShipping />
            </span>
            <div>
              <span className="hero-section__float-label">On-Time Rate</span>
              <span className="hero-section__float-value hero-section__float-value--sm">98.5%</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
